import { useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { parseCheckoutParams } from './useCheckoutParams';

export const getSessionId = () => {
  const key = 'form_tracking_session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
};

export const getPersistentId = () => {
  const key = 'form_tracking_user';
  let id = localStorage.getItem(key);
  if (!id) {
    id = `u-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem(key, id);
  }
  return id;
};

// Entidad (UAI, CEE, ALB…) y tipo de programa (g/p/e/m) del link actual. Van en todo lo que se
// trackea: sin ellos, los eventos de colegios quedaban mezclados con los de la universidad y no
// habia forma de separarlos (el codigo de carrera puede repetirse entre entidades). T-2026-107.
const getEntidadYTipo = (search) => {
  const { e, t } = parseCheckoutParams(search);
  return { entidad: e, tipo: t };
};

// Corta TODA notificación de progreso hacia afuera (tracking + identity + lead de Zoho, que es el
// que hace que un asesor reciba "se quiere inscribir"). Para probar contra prod desde local:
// REACT_APP_DISABLE_TRACKING=true en .env.development.
// Es opt-out a propósito: si la variable no está, el tracking anda. Y CRA la resuelve en tiempo de
// BUILD, así que no es un switch en caliente del sitio publicado (ver nota en el README del cambio).
export const trackingDeshabilitado = () => process.env.REACT_APP_DISABLE_TRACKING === 'true';

export const identifyUser = (email, programData = {}) => {
  if (trackingDeshabilitado()) return Promise.resolve();
  sessionStorage.setItem('form_tracking_email', email);
  return fetch(`${process.env.REACT_APP_API_BASE_URL}/tracking/identity`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      appCode:      process.env.REACT_APP_TRACKING_CODE || null,
      sessionId:    getSessionId(),
      persistentId: getPersistentId(),
      email,
      ...getEntidadYTipo(window.location.search),
      ...programData,
    }),
  }).catch(() => {});
};

const useFormTracking = (stepNumber = 0) => {
  const sessionId    = useRef(getSessionId());
  const persistentId = useRef(getPersistentId());
  const stepStartTime = useRef(Date.now());
  const { search } = useLocation();
  const { entidad, tipo } = getEntidadYTipo(search);

  const send = (payload) => {
    if (trackingDeshabilitado()) return;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/tracking/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  };

  const track = useCallback(
    (eventName, { fieldName = null } = {}) => {
      const timeSpent = Math.round((Date.now() - stepStartTime.current) / 1000);
      send({
        appCode:      process.env.REACT_APP_TRACKING_CODE || null,
        sessionId:    sessionId.current,
        persistentId: persistentId.current,
        eventName,
        fieldName,
        stepNumber,
        timeSpent,
        entidad,
        tipo,
      });
    },
    [stepNumber, entidad, tipo]
  );

  const resetStepTimer = useCallback(() => {
    stepStartTime.current = Date.now();
  }, []);

  useEffect(() => {
    if (trackingDeshabilitado()) return;
    const handleAbandonment = () =>
      navigator.sendBeacon(
        `${process.env.REACT_APP_API_BASE_URL}/tracking/event`,
        new Blob(
          [JSON.stringify({
            appCode:      process.env.REACT_APP_TRACKING_CODE || null,
            sessionId:    sessionId.current,
            persistentId: persistentId.current,
            eventName:    'form_abandoned',
            fieldName:    null,
            stepNumber,
            timeSpent:    Math.round((Date.now() - stepStartTime.current) / 1000),
            entidad,
            tipo,
          })],
          { type: 'application/json' }
        )
      );

    window.addEventListener('beforeunload', handleAbandonment);
    return () => window.removeEventListener('beforeunload', handleAbandonment);
  }, [stepNumber, entidad, tipo]);

  return { track, resetStepTimer };
};

export default useFormTracking;
